import { ref, watch } from "vue";
import { useArtifiPreview, argsComplete } from "./useArtifiPreview";
import { droppedPlacementCodes } from "@/components/decoration/previewWarningCopy";
import { COLORWAY_STATE } from "@/utils/previewColorway";
import {
    useArtifiPreviewCacheStore,
    buildCacheKey,
} from "@/stores/artifiPreviewCache";

/**
 * useArtifiPreviewCache — wraps useArtifiPreview with an LRU URL cache.
 *
 * Day 2 (canonical product editor). Public surface MATCHES useArtifiPreview so the Stage
 * (and any future caller) can swap in this composable without other code
 * changes:
 *
 *   const { url, loading, error, warnings, refresh } = useArtifiPreviewCache(argsRef)
 *
 * argsRef (richer than useArtifiPreview's bare tuple):
 *   ref<{
 *     productSku: string,
 *     colorSku: string,
 *     viewCode: string,
 *     artworkFileId: string,
 *     variantSku: string,          // resolved by caller per LivePreviewCard
 *                                  // convention: productItemNumber ?? v.sku
 *     variantBindings: Record<variantKey,
 *                             {artworkFileId, colorwayId}|artworkFileId|null>,
 *                                  // the bare form is the pre-colorway
 *                                  // shape and is still accepted
 *   } | null>
 *
 * Behavior:
 *   - cache HIT: returns the cached URL synchronously, loading=false.
 *     The underlying composable is instantiated (we need its refs) but
 *     its argsRef stays null, so no network call fires.
 *   - cache MISS: extracts the (variantSku, viewCode, artworkFileId) subset
 *     and pushes it through to useArtifiPreview's argsRef. When url
 *     resolves, persists to the cache before exposing.
 *   - colorwayState: the server's `colorway.state` for the CURRENT key's
 *     render ('applied' | 'pending' | 'unavailable' | null). Only a render
 *     that is not `pending`/`unavailable` is cached: those two are the
 *     DEFAULT colors, and caching one under the colorway's key would serve
 *     the wrong picture for the life of the page.
 *   - refresh(): evicts the current cache entry (if any) then delegates
 *     to inner.refresh() so the underlying retry path runs.
 *   - null/incomplete args clears state and does not fetch (mirrors the
 *     underlying composable's null-args behavior).
 */
export function useArtifiPreviewCache(cacheArgsRef) {
    const cacheStore = useArtifiPreviewCacheStore();

    const url = ref(null);
    const loading = ref(false);
    const error = ref(null);
    const warnings = ref([]);
    const colorwayState = ref(null);
    // The key the inner composable was last asked to render. Unlike
    // `inflightKey` it survives the resolution, so a LATE state change (a
    // `pending` render becoming `unavailable` with the same image) can still
    // be mirrored while the user is looking at that key.
    let delegatedKey = null;

    // The args ref we hand to useArtifiPreview. We only push completed
    // args here on a cache MISS — on a HIT, this stays null so the
    // underlying watch doesn't fire and no network call happens.
    const innerArgs = ref(null);

    // P0 fix (gate review round 1) — capture the cache key for the
    // currently-dispatched MISS. When the inner composable resolves
    // late (args have already changed to a different key, possibly a
    // HIT), the inner.url watch writes the late URL under THIS
    // captured key (the key the request was made FOR), not under
    // whatever cacheArgsRef now points at. That's still legitimate
    // caching (the URL IS correct for that key); the bug was writing
    // late resolutions under a DIFFERENT key, which poisoned the cache
    // AND flashed the wrong image into the wrapper's url.value.
    //
    // null when no delegation is active. Guards every inner-watcher
    // write below.
    let inflightKey = null;

    const inner = useArtifiPreview(innerArgs);

    // When the inner composable resolves a url, persist to cache and
    // mirror onto the wrapper's exposed state — but ONLY if we still
    // have an inflight key (delegation is active) AND the args haven't
    // moved away from the inflight key.
    //
    // Gate round 2 P1 fix: only clear `inflightKey` when the resolution
    // corresponds to the CURRENT state (currentKey === inflightKey).
    // For genuinely stale resolutions (user already moved on), leave
    // inflightKey alone so subsequent legitimate resolutions on the
    // active key still mirror correctly. The inner composable's own
    // currentKey-guard suppresses most stale fires already; this
    // wrapper-level rule is defense in depth.
    watch(inner.url, (newUrl) => {
        if (newUrl == null) return;
        if (inflightKey == null) return; // not delegating anymore
        const currentArgs = cacheArgsRef.value;
        const currentKey = currentArgs ? buildCacheKey(currentArgs) : null;

        // Persist the resolved URL under the key it was dispatched for
        // (inflightKey). This is correct even if args have moved on —
        // a future revisit to the same key gets a cache hit.
        // #312: a composite that dropped a placement is shown but never
        // cached; the cache is shared with the Product Listing, which would
        // serve the partial image as the finished render on a revisit.
        // A colorway render that is not `applied` (pending / unavailable) is
        // the default colors: shown, never cached.
        const innerState = inner.colorwayState?.value ?? null;
        const colorwayNotApplied =
            innerState === COLORWAY_STATE.PENDING ||
            innerState === COLORWAY_STATE.UNAVAILABLE;
        if (
            droppedPlacementCodes(inner.warnings.value).length === 0 &&
            !colorwayNotApplied
        ) {
            cacheStore.set(inflightKey, newUrl);
        }

        // Only mirror to the wrapper's exposed state when the user is
        // still looking at the same key. If they've already moved on
        // (e.g. clicked a different color and got a HIT in the
        // meantime), DO NOT clobber the wrapper's url.value with the
        // stale resolution — the main watcher already swapped to the
        // new key's URL.
        if (currentKey === inflightKey) {
            url.value = newUrl;
            colorwayState.value = innerState;
            loading.value = false;
            error.value = null;
            warnings.value = inner.warnings.value ?? [];
            // Active resolution for the current key completed; clear
            // inflight so subsequent stray fires are no-ops -- EXCEPT while
            // the colorway is `pending`: the inner composable re-asks, and
            // the applied render that follows must still land and be cached.
            if (innerState !== COLORWAY_STATE.PENDING) {
                inflightKey = null;
            }
        }
        // Stale resolution path: DO NOT clear inflightKey. The active
        // (still-current) key's request may still be pending and we
        // need its resolution to mirror correctly.
    });

    // Mirror the colorway state for the key the user is looking at.
    if (inner.colorwayState) {
        watch(inner.colorwayState, (state) => {
            if (delegatedKey == null) return;
            const currentArgs = cacheArgsRef.value;
            const currentKey = currentArgs ? buildCacheKey(currentArgs) : null;
            if (currentKey === delegatedKey) {
                colorwayState.value = state ?? null;
            }
        });
    }

    // Surface inner errors — same guard. Only mirror when the user is
    // still on the dispatched key; otherwise drop the error silently
    // (user already moved on). Mirror the clear-inflight gating from
    // the url watcher.
    watch(inner.error, (newError) => {
        if (newError == null) return;
        if (inflightKey == null) return;
        const currentArgs = cacheArgsRef.value;
        const currentKey = currentArgs ? buildCacheKey(currentArgs) : null;
        if (currentKey === inflightKey) {
            error.value = newError;
            colorwayState.value = null;
            loading.value = false;
            // Clear any stale url from a PREVIOUS resolution (card
            // CH2XJ9Jq). The active-key request failed (e.g. a preview 404
            // with hint=render_failed / sku_not_configured); leaving the
            // previous color's url in place made the Stage render
            // v-else-if="previewUrl" (the stale image) instead of falling
            // through to the honest placeholder tile -- the "loading then
            // revert to previous image" bug. Null it so previewFallback wins.
            url.value = null;
            inflightKey = null;
        }
        // Stale-error path: DO NOT clear inflightKey for the same reason
        // as the url watcher above.
    });

    // Reflect inner loading state ONLY when we delegated (innerArgs set).
    // Otherwise the inner composable's loading flips during construction
    // and would mask cache-hit synchronous resolution.
    watch(inner.loading, (l) => {
        if (innerArgs.value != null) {
            loading.value = l;
        }
    });

    // Main watcher: on every cacheArgsRef change, check cache first.
    watch(
        cacheArgsRef,
        (newArgs) => {
            // Incomplete args (no variant SKU or no view code -- e.g. a
            // non-SanMar product, which has no Artifi views) are never
            // fetched by the inner composable, which resets WITHOUT flipping
            // its own loading. Treating them as a miss here latched
            // loading=true forever, so the Stage shimmered indefinitely and
            // its stitch-render fallback was unreachable (found 2026-10-07).
            // Same question the inner composable asks; same answer.
            if (!newArgs || !argsComplete(newArgs)) {
                url.value = null;
                loading.value = false;
                error.value = null;
                warnings.value = [];
                colorwayState.value = null;
                innerArgs.value = null;
                inflightKey = null;
                delegatedKey = null;
                return;
            }
            const key = buildCacheKey(newArgs);
            if (cacheStore.has(key)) {
                // Cache HIT — serve synchronously, suppress delegation.
                // cacheStore.get() also performs the LRU touch (re-insert
                // to most-recent end of insertion order).
                url.value = cacheStore.get(key);
                loading.value = false;
                error.value = null;
                warnings.value = [];
                // Only an `applied` (or colorway-free) render is ever cached.
                colorwayState.value = null;
                delegatedKey = null;
                innerArgs.value = null;
                // Clear any prior inflight; if its resolution lands
                // later, the watchers above will see inflightKey=null
                // and drop the result (cache write skipped, wrapper
                // state not clobbered).
                inflightKey = null;
                return;
            }
            // Cache MISS — delegate to inner composable. Capture the
            // key we're dispatching FOR; the inner.url/error watchers
            // use this to decide where to write the resolution and
            // whether to mirror to the wrapper.
            inflightKey = key;
            delegatedKey = key;
            colorwayState.value = null;
            loading.value = true;
            error.value = null;
            innerArgs.value = {
                variantSku: newArgs.variantSku,
                viewCode: newArgs.viewCode,
                artworkFileId: newArgs.artworkFileId,
                // 2026-06-19 Option C: forward the resolution params so
                // the backend can resolve SanMar color label -> Artifi
                // numeric variant SKU when the client-side coverage map
                // doesn't have it (e.g. cases requiring the CSV reverse
                // lookup like "Graphite Heather" -> "GphHeather").
                masterSku: newArgs.masterSku,
                colorName: newArgs.colorName,
                // Multi-placement composite identity (card
                // https://trello.com/c/pI48s0zf): forward so the inner
                // composable requests the composite render.
                campaignProductId: newArgs.campaignProductId,
                // ADR 0065 amendment: consumer tag for meter attribution +
                // the server-side 90% budget gate. NOT part of the cache key
                // -- the rendered pixels are identical regardless of which
                // surface warmed them, so a decorations_page render and an
                // editor render for the same tuple share ONE cache entry
                // (fewer Artifi calls). ``source`` only affects the LIVE
                // request's gating on a cache MISS.
                source: newArgs.source,
                // The placement gate's input (requestArtifiPreview refuses a
                // product-scoped request without at least one placement).
                placementCount: newArgs.placementCount,
                // The placement being previewed: the SERVER derives the
                // colorway from it. colorwayId is request identity only (it
                // is never sent); both are in the cache key via
                // variantBindings.
                placementId: newArgs.placementId,
                colorwayId: newArgs.colorwayId,
            };
        },
        { immediate: true, deep: true },
    );

    /**
     * Force a re-fetch with the current args. Evicts the cache entry for
     * the current key (if any), then calls the underlying refresh() which
     * re-runs the dispatch path. No-op on the cache side if args are null
     * — but we still delegate to inner.refresh() so its own no-op path
     * (no lastArgs) runs consistently with direct useArtifiPreview usage.
     */
    function refresh() {
        const args = cacheArgsRef.value;
        if (args) {
            const key = buildCacheKey(args);
            if (cacheStore.has(key)) {
                cacheStore.delete(key);
            }
            // Refresh dispatches a new request for the current args; set
            // inflightKey so the resolution mirrors back to the wrapper.
            inflightKey = key;
        }
        inner.refresh();
    }

    return { url, loading, error, warnings, colorwayState, refresh };
}

export default useArtifiPreviewCache;
